import { useContext } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogProps,
  Input,
  Link,
  Notification,
  Select,
  Text,
} from '@bitrise/bitkit';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import CountrySelector from '@/components/CountrySelector/CountrySelector';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { getExtendLimitPath, planSelectorPagePath } from '@/utils/monolithApiRouteService';
import { SimpleErrorResponse } from '@/models/Error';

const COMPANY_SIZE_OPTIONS = ['1-500', '501-750', '751-5000', '5001-10000', '10000+'];

type Inputs = {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  companyName: string;
  companySize: string;
  country: string;
};

type ExtendYourLimitDialogProps = Omit<DialogProps, 'title'>;

const ExtendYourLimitDialog = (props: ExtendYourLimitDialogProps) => {
  const { onClose, ...dialogProps } = props;

  const {
    account: { slug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const { control, handleSubmit } = useForm<Inputs>({
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      companyName: '',
      companySize: '',
      country: '',
    },
  });

  const { mutate, isError, isPending, error } = useMonolithApiMutation<unknown, SimpleErrorResponse, unknown>(
    getExtendLimitPath(slug),
    'post',
    {
      onSuccess: () => {
        if (onClose) {
          onClose();
        }
        window.location.assign(window.location.origin);
      },
    },
  );

  let errorMsg;
  switch (error?.response?.data?.errorMsg) {
    case 'not_on_time_based_trial': {
      errorMsg = (
        <>
          Your Workspace is not on a time based trial.{' '}
          <Link isExternal isUnderlined href="https://www.bitrise.io/contact">
            Contact support
          </Link>
        </>
      );
      break;
    }
    case 'time_based_trial_limit_already_extended': {
      errorMsg = (
        <>
          Your Workspace already extended the credit limit for your trial.{' '}
          <Link isExternal isUnderlined href={planSelectorPagePath(slug)}>
            Select a plan
          </Link>{' '}
          or{' '}
          <Link isExternal isUnderlined href="https://www.bitrise.io/contact">
            contact sales
          </Link>{' '}
          to continue using Bitrise.
        </>
      );
      break;
    }
    default: {
      errorMsg = (
        <>
          Submit and extend failed. Please try again. If the problem still persists,{' '}
          <Link isExternal isUnderlined href="https://www.bitrise.io/contact">
            contact support
          </Link>
          .
        </>
      );
    }
  }

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    mutate(data);
  };

  return (
    <Dialog
      onClose={onClose}
      {...dialogProps}
      as="form"
      isClosable={!isPending}
      onSubmit={handleSubmit(onSubmit)}
      title="Extend your limit"
    >
      <DialogBody>
        <Text marginBlockEnd="24">
          Your workspace usage has exceeded our fair usage limits. Submit this form to extend your limits and continue
          exploring Bitrise.
        </Text>
        <Box display="flex" gap="24" marginBottom="24">
          <Controller
            control={control}
            name="firstName"
            render={({ field: { ref, ...fieldProps } }) => (
              <Input inputRef={ref} flex={1} isRequired label="First name" {...fieldProps} />
            )}
          />
          <Controller
            control={control}
            name="lastName"
            render={({ field: { ref, ...fieldProps } }) => (
              <Input inputRef={ref} flex={1} isRequired label="Last name" {...fieldProps} />
            )}
          />
        </Box>
        <Controller
          control={control}
          name="phoneNumber"
          render={({ field: { ref, ...fieldProps } }) => (
            <Input inputRef={ref} label="Phone number" marginBottom="24" {...fieldProps} />
          )}
        />
        <Controller
          control={control}
          name="companyName"
          render={({ field: { ref, ...fieldProps } }) => (
            <Input inputRef={ref} isRequired label="Company name" marginBottom="24" {...fieldProps} />
          )}
        />
        <Controller
          control={control}
          name="companySize"
          render={({ field }) => (
            <Select isRequired label="Company size" marginBottom="24" placeholder="Select an option" {...field}>
              {COMPANY_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          )}
        />
        <Controller
          control={control}
          name="country"
          render={({ field }) => <CountrySelector isRequired {...field} />}
        />
        {isError && (
          <Notification marginBlockStart="24" status="error">
            {errorMsg}
          </Notification>
        )}
      </DialogBody>
      <DialogFooter>
        <Button isDisabled={isPending} onClick={onClose} variant="secondary">
          Cancel
        </Button>
        <Button isLoading={isPending} type="submit">
          Submit & extend
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default ExtendYourLimitDialog;
